import { Laptop, Cloud, ArrowUpRight, ArrowDownLeft, Clock, BookOpen } from 'lucide-react'
import ModuleLayout from '../components/ModuleLayout'
import PagedLesson from '../components/PagedLesson'
import CopyBlock from '../components/CopyBlock'
import { flattenQuestions } from '../lib/lessonScoring'

function Intro({ pageCount, questionCount }) {
  return (
    <section className="rounded-xl border-2 border-indigo-200 bg-indigo-50/50 p-6">
      <h2 className="mb-2 text-2xl font-bold text-slate-800">GitHub 101: How Code & Changes Live</h2>
      <p className="mb-3 text-slate-700">
        Whether you are writing code, configuring tools, or working with AI agents, understanding
        how GitHub SEED_COURSES changes and keeps teams in sync is a core superpower.
      </p>
      <p className="mb-4 text-slate-700">
        {pageCount} structured points covering repositories, branches, where your files actually live,
        and the push/pull cycle — each with quick checks so you know it landed.
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-indigo-500" />
          About 12 minutes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <BookOpen className="h-4 w-4 text-indigo-500" />
          {pageCount} points · {questionCount} questions · 70% to pass
        </span>
      </div>
    </section>
  )
}

function Callout({ children, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-300 bg-slate-50 text-slate-700',
    amber: 'border-amber-400 bg-amber-50 text-amber-900',
    indigo: 'border-indigo-300 bg-indigo-50/60 text-slate-700',
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  }
  return (
    <div className={`rounded-lg border-l-4 py-3 pl-4 pr-3 text-sm ${tones[tone]}`}>{children}</div>
  )
}

const PAGES = [
  {
    title: 'The Core Building Blocks',
    content: (
      <>
        <p>
          Git is a version control system that records every change made to a project over time.
          GitHub is the cloud platform where those projects are hosted and shared with the team.
        </p>
        <p>
          There are four fundamental terms you will use every single day:
        </p>

        <div className="my-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-bold text-slate-700">GitHub Term</th>
                <th className="px-4 py-3 text-left font-bold text-slate-700">What It Is</th>
                <th className="px-4 py-3 text-left font-bold text-slate-700">How to Think of It</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              <tr>
                <td className="px-4 py-3 font-mono font-bold text-indigo-600">Repository (Repo)</td>
                <td className="px-4 py-3">The project's dedicated folder & database.</td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  <strong>The App's Home</strong> — Contains all source code, assets, settings, and the permanent history of every edit ever made.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono font-bold text-indigo-600">Main Branch</td>
                <td className="px-4 py-3">The default, production-ready timeline.</td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  <strong>The Live App</strong> — The stable, tested code currently running for users. Kept protected from untested edits.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono font-bold text-indigo-600">Branch</td>
                <td className="px-4 py-3">An isolated copy of code for new work.</td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  <strong>A Safe Sandbox</strong> — A private workspace where you build a feature without risking breaking the live app for anyone else.
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono font-bold text-indigo-600">Commit</td>
                <td className="px-4 py-3">A permanent, recorded snapshot of changes.</td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  <strong>A Saved Checkpoint</strong> — A timestamped save point with a message (e.g. <em>"Fixed mobile button layout"</em>), letting you roll back anytime.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout tone="indigo">
          <strong>The Golden Rule:</strong> Never edit the <code className="rounded bg-indigo-100 px-1 py-0.5 font-mono text-xs">main</code> branch directly.
          Always create a branch, test your changes, and merge them in once verified.
        </Callout>
      </>
    ),
    questions: [
      {
        question: 'Why do developers create a branch instead of making changes directly on main?',
        options: [
          'Because Git does not allow more than 1 person to open the repository',
          'To work in an isolated sandbox and avoid breaking the stable live application',
          'To permanently hide their code from teammates',
          'Because branches make the code run faster',
        ],
        correctIndex: 1,
        explanation:
          'Branches isolate experimental or in-progress work from the stable main branch until it is reviewed and tested.',
      },
      {
        question: 'What is a Git commit?',
        options: [
          'A command that permanently deletes a file',
          'A scheduled meeting with your team',
          'A recorded snapshot of your changes with an explanatory message',
          'A link to a Google Doc',
        ],
        correctIndex: 2,
        explanation:
          'A commit records the exact changes you made alongside a message explaining why, creating a permanent checkpoint.',
      },
    ],
  },

  {
    title: 'Where Code Lives: Local vs Cloud',
    content: (
      <>
        <p>
          A common beginner confusion is: <em>"When I create a branch or edit a file, is it on my computer or on GitHub?"</em>
        </p>
        <p>
          Git is a <strong>distributed</strong> system, which means code lives in two distinct locations:
        </p>

        <div className="my-5 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-2xs">
            <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm mb-2">
              <Laptop className="h-4 w-4" />
              1. Your Local Machine (Laptop)
            </div>
            <ul className="text-xs text-slate-600 space-y-2 list-disc pl-4">
              <li>All project files live in your local folder on your hard drive.</li>
              <li>Your history and local branches live in the hidden <code className="bg-slate-100 px-1 py-0.5 rounded font-mono">.git</code> folder.</li>
              <li>Creating a branch or making a commit is <strong>instant and offline</strong>.</li>
              <li><strong>Nobody else can see it</strong> until you choose to upload it.</li>
            </ul>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-2xs">
            <div className="flex items-center gap-2 text-purple-600 font-bold text-sm mb-2">
              <Cloud className="h-4 w-4" />
              2. GitHub Cloud (Remote)
            </div>
            <ul className="text-xs text-slate-600 space-y-2 list-disc pl-4">
              <li>The central shared repository hosted on <code className="bg-slate-100 px-1 py-0.5 rounded font-mono">github.com</code>.</li>
              <li>Visible to your teammates and automated build tools.</li>
              <li>Acts as the single source of truth for the project.</li>
              <li>Powers Pull Requests, code reviews, and automated testing (CI/CD).</li>
            </ul>
          </div>
        </div>

        <Callout tone="amber">
          If you create a branch or make commits on your laptop and your laptop runs out of battery,
          those commits are safe on your drive — but they are <strong>not on GitHub</strong> until you push them!
        </Callout>
      </>
    ),
    questions: [
      {
        question: 'You create a branch and make 3 commits on your laptop. Who can see these changes before you push?',
        options: [
          'Everyone at Finder on GitHub',
          'Only you on your local machine',
          'Anyone who has the repository open in their browser',
          'The deployment server automatically',
        ],
        correctIndex: 1,
        explanation:
          'Local branches and commits remain strictly on your machine until you explicitly push them to GitHub.',
      },
      {
        question: 'Where is the local Git history stored on your computer?',
        options: [
          'In your browser cookies',
          'In the hidden .git folder inside your project directory',
          'In your OneDrive recycling bin',
          'In the cloud only',
        ],
        correctIndex: 1,
        explanation:
          'Every Git repo contains a hidden .git folder at its root where local commit history and branch pointers are stored.',
      },
    ],
  },

  {
    title: 'The Bridge: Pushing & Pulling',
    content: (
      <>
        <p>
          To synchronize code between your laptop and GitHub in the cloud, you use two fundamental actions:
        </p>

        <div className="my-5 space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-center gap-2 font-bold text-emerald-900 text-sm mb-1">
              <ArrowUpRight className="h-4 w-4 text-emerald-600" />
              git push (Upload to GitHub)
            </div>
            <p className="text-xs text-emerald-800 leading-relaxed mb-2">
              Sends your local commits and branches from your laptop <strong>up to GitHub</strong>.
              Use this when you want to share your progress, back up your branch, or open a Pull Request.
            </p>
            <CopyBlock text="git push origin feature-my-update" label="Push branch" />
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
            <div className="flex items-center gap-2 font-bold text-blue-900 text-sm mb-1">
              <ArrowDownLeft className="h-4 w-4 text-blue-600" />
              git pull (Download from GitHub)
            </div>
            <p className="text-xs text-blue-800 leading-relaxed mb-2">
              Fetches the newest commits from GitHub and merges them <strong>down to your laptop</strong>.
              Use this at the start of every session so you always have your teammates' latest work.
            </p>
            <CopyBlock text="git pull origin main" label="Pull latest main" />
          </div>
        </div>

        <Callout tone="emerald">
          <strong>Best Practice:</strong> Always run <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">git pull</code> before
          starting a new branch so you are building on top of the freshest code.
        </Callout>
      </>
    ),
    questions: [
      {
        question: 'What command uploads your local commits and branch to GitHub in the cloud?',
        options: [
          'git pull',
          'git push',
          'git delete',
          'git status',
        ],
        correctIndex: 1,
        explanation:
          'git push uploads your commits to the remote repository on GitHub.',
      },
      {
        question: 'Why should you run git pull before creating a new feature branch?',
        options: [
          'To make sure your local repository has the latest approved code from your teammates',
          'To delete your local files',
          'Because Git will crash otherwise',
          'To sign out of your account',
        ],
        correctIndex: 0,
        explanation:
          'Pulling first ensures you start your work from the latest version of main, avoiding merge conflicts later.',
      },
    ],
  },

  {
    title: 'The 5-Step Team Workflow',
    content: (
      <>
        <p>
          In professional development at Finder, all changes follow this standard 5-step lifecycle:
        </p>

        <div className="my-5 space-y-3">
          {[
            {
              step: '1',
              title: 'Pull Latest Main',
              desc: 'Ensure your laptop is up to date with the latest code from GitHub.',
              code: 'git checkout main && git pull',
            },
            {
              step: '2',
              title: 'Create Your Branch',
              desc: 'Create an isolated sandbox branch for your feature or bug fix.',
              code: 'git checkout -b feat/add-discount-calculator',
            },
            {
              step: '3',
              title: 'Make Commits As You Build',
              desc: 'Save progress with clear messages as you complete each part of the task.',
              code: 'git commit -m "feat(calc): implement base formula"',
            },
            {
              step: '4',
              title: 'Push Branch to GitHub',
              desc: 'Upload your branch so GitHub has a copy for review.',
              code: 'git push -u origin feat/add-discount-calculator',
            },
            {
              step: '5',
              title: 'Open Pull Request (PR) & Merge',
              desc: 'Your teammates review the code, automated tests pass, and it merges into main!',
              code: 'gh pr create --title "Add discount calculator" --body "..."',
            },
          ].map((item) => (
            <div key={item.step} className="rounded-xl border border-slate-200 bg-white p-4 shadow-2xs">
              <div className="flex items-center gap-3 mb-1.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                  {item.step}
                </span>
                <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
              </div>
              <p className="text-xs text-slate-600 ml-9 mb-2">{item.desc}</p>
              <div className="ml-9">
                <CopyBlock text={item.code} />
              </div>
            </div>
          ))}
        </div>

        <Callout tone="indigo">
          <strong>What is a Pull Request (PR)?</strong> It's a formal request asking your team to
          review your branch and merge it into <code className="rounded bg-indigo-100 px-1 py-0.5 font-mono text-xs">main</code>.
          This is where peer reviews, quality checks, and automated CI tests happen.
        </Callout>
      </>
    ),
    questions: [
      {
        question: 'What is the purpose of a Pull Request (PR)?',
        options: [
          'To request a physical copy of the code from the IT department',
          'To propose merging your branch into main, enabling code review and automated testing',
          'To delete the repository permanently',
          'To lock the computer while you are away',
        ],
        correctIndex: 1,
        explanation:
          'A Pull Request invites teammates to review and discuss your changes before merging them safely into main.',
      },
      {
        question: 'What happens when a Pull Request is approved and merged?',
        options: [
          'The changes in your branch become part of the official main branch',
          'All other branches in the repository are erased',
          'The repository is archived and cannot be edited',
          'Nothing, it is only for display',
        ],
        correctIndex: 0,
        explanation:
          'Merging incorporates all commits from your feature branch into the main branch, updating the live project.',
      },
    ],
  },
]

export default function ModuleGithub101() {
  return (
    <ModuleLayout moduleId={26}>
      <div className="space-y-10">
        <Intro pageCount={PAGES.length} questionCount={flattenQuestions(PAGES).length} />
        <PagedLesson moduleId={26} pages={PAGES} />
      </div>
    </ModuleLayout>
  )
}
